import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { PaperAcknowledgementResult } from '@up2d8/contracts';
import { Button, Dialog, Input, useToast } from '@up2d8/ui';
import { AdminIcon, Btn, Checkbox, Field, Input as AdminInput, Modal } from '../admin-kit';
import { downloadFile } from '../../shared/download';
import { API_BASE, useSession } from '../../shared/session';

function today(): string {
  const date = new Date();
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(
    date.getDate(),
  ).padStart(2, '0')}`;
}

/**
 * De afvinklijst op papier (Stap 42a): afdrukken, en de handtekeningen achteraf registreren.
 *
 * Twee omlijnde knoppen, op de bereikpagina in het beheer en bij een bericht in Mijn team. Het
 * registreren is een lijst met vinkjes en de dag van ondertekening; meer niet. Wat al bevestigd
 * is (in de app, op een kiosk), staat niet in de lijst: de eerste bevestiging blijft staan.
 *
 * `variant="admin"` is de vorm van het beheerportaal (BHR-4, B3): "Handtekeningen registreren" als
 * tekstknop, "Afvinklijst" omlijnd met een printer, en het registreren in een venster van de kit.
 * Mijn team houdt de vorm van de app.
 */
export function PaperChecklist({
  postId,
  teamId,
  people,
  onRecorded,
  variant = 'app',
}: {
  postId: string;
  teamId: string | null;
  /** Wie nog niet bevestigde, binnen wat de kijker mag zien. */
  people: { personId: string; displayName: string }[];
  onRecorded?: () => void;
  variant?: 'app' | 'admin';
}) {
  const { t } = useTranslation();
  const { api, getAccessToken } = useSession();
  const queryClient = useQueryClient();
  const toast = useToast();
  const [open, setOpen] = useState(false);
  const [chosen, setChosen] = useState<string[]>([]);
  const [signedOn, setSignedOn] = useState(today());

  const download = useMutation({
    mutationFn: () =>
      downloadFile({
        path: `/api/v1/reach/posts/${postId}/checklist.pdf${teamId ? `?teamId=${encodeURIComponent(teamId)}` : ''}`,
        fileName: `afvinklijst-${postId.slice(0, 8)}.pdf`,
        token: getAccessToken(),
        baseUrl: API_BASE,
      }),
    onError: () => toast.show({ title: t('paper.failed'), tone: 'danger' }),
  });

  const record = useMutation({
    mutationFn: async () => {
      const { data, error } = await api.POST(
        '/api/v1/reach/posts/{postId}/paper-acknowledgements',
        {
          params: { path: { postId } },
          body: { personIds: chosen, signedOn } as never,
        },
      );
      if (error || !data) throw error ?? new Error('Mislukt');
      return data as PaperAcknowledgementResult;
    },
    onSuccess: async (result) => {
      setOpen(false);
      setChosen([]);
      await queryClient.invalidateQueries({ queryKey: ['reach', postId] });
      await queryClient.invalidateQueries({ queryKey: ['my-team-post', postId] });
      toast.show({ title: t('paper.recorded', { count: result.recorded }), tone: 'success' });
      onRecorded?.();
    },
    onError: () => toast.show({ title: t('paper.failed'), tone: 'danger' }),
  });

  const toggle = (personId: string, checked: boolean): void =>
    setChosen((current) =>
      checked ? [...current, personId] : current.filter((id) => id !== personId),
    );

  if (variant === 'admin') {
    return (
      <>
        {people.length > 0 ? (
          <Btn
            variant="ghost"
            icon={<AdminIcon name="pen" />}
            onClick={() => setOpen(true)}
            testId="paper-record"
          >
            {t('paper.record')}
          </Btn>
        ) : null}
        <Btn
          variant="sec"
          icon={<AdminIcon name="printer" />}
          disabled={download.isPending}
          onClick={() => download.mutate()}
          testId="paper-download"
        >
          {t('paper.download')}
        </Btn>
        <Modal
          open={open}
          onClose={() => setOpen(false)}
          title={t('paper.recordTitle')}
          sub={t('paper.recordHint')}
          testId="paper-dialog"
          footer={
            <>
              <Btn variant="sec" onClick={() => setOpen(false)}>
                {t('common.cancel')}
              </Btn>
              <Btn
                disabled={chosen.length === 0 || !signedOn || record.isPending}
                onClick={() => record.mutate()}
                testId="paper-confirm"
              >
                {t('paper.confirm', { count: chosen.length })}
              </Btn>
            </>
          }
        >
          <div className="a-checklist">
            {people.map((person) => (
              <Checkbox
                key={person.personId}
                checked={chosen.includes(person.personId)}
                onChange={(checked) => toggle(person.personId, checked)}
                label={person.displayName}
              />
            ))}
          </div>
          <Field label={t('paper.signedOn')}>
            {(props) => (
              <AdminInput
                {...props}
                type="date"
                value={signedOn}
                max={today()}
                onChange={(event) => setSignedOn(event.target.value)}
                style={{ maxWidth: 200 }}
              />
            )}
          </Field>
        </Modal>
      </>
    );
  }

  return (
    <>
      <Button
        variant="secondary"
        loading={download.isPending}
        onClick={() => download.mutate()}
        data-testid="paper-download"
      >
        {t('paper.download')}
      </Button>
      {people.length > 0 ? (
        <Button variant="secondary" onClick={() => setOpen(true)} data-testid="paper-record">
          {t('paper.record')}
        </Button>
      ) : null}
      <Dialog
        open={open}
        onOpenChange={setOpen}
        title={t('paper.recordTitle')}
        description={t('paper.recordHint')}
      >
        <div className="flex flex-col gap-element" data-testid="paper-dialog">
          <ul className="flex max-h-80 flex-col divide-y divide-border-light overflow-y-auto">
            {people.map((person) => (
              <li key={person.personId}>
                <label className="flex min-h-12 items-center gap-element py-2">
                  <input
                    type="checkbox"
                    className="h-5 w-5 rounded border-border text-brand-primary"
                    checked={chosen.includes(person.personId)}
                    onChange={(event) => toggle(person.personId, event.target.checked)}
                  />
                  <span className="text-content text-text-primary">{person.displayName}</span>
                </label>
              </li>
            ))}
          </ul>
          <div className="w-48">
            <Input
              label={t('paper.signedOn')}
              type="date"
              value={signedOn}
              max={today()}
              onChange={(event) => setSignedOn(event.target.value)}
            />
          </div>
          <div className="flex justify-end gap-inline">
            <Button variant="secondary" onClick={() => setOpen(false)}>
              {t('common.cancel')}
            </Button>
            <Button
              disabled={chosen.length === 0 || !signedOn}
              loading={record.isPending}
              onClick={() => record.mutate()}
              data-testid="paper-confirm"
            >
              {t('paper.confirm', { count: chosen.length })}
            </Button>
          </div>
        </div>
      </Dialog>
    </>
  );
}
